import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { discoverLeads } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Discover() {
  const [keywords, setKeywords] = useState("");
  const [count, setCount] = useState(6);
  const [industries, setIndustries] = useState("");
  const [loc1, setLoc1] = useState("");
  const [loc2, setLoc2] = useState("");
  const [loc3, setLoc3] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  const onRun = async () => {
    setLoading(true);
    try {
      const data = await discoverLeads({
        keywords,
        count: Number(count) || 6,
        industries: industries.split(",").map((s) => s.trim()).filter(Boolean),
        locations: [loc1, loc2, loc3].map((s) => s.trim()).filter(Boolean),
      });
      setResults(data);
      toast.success(`${data.length} prospect${data.length === 1 ? "" : "s"} added to pipeline.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="px-12 py-10 border-b border-[#222]">
        <div className="uppercase-mono text-[#8a8a8a]">Section 02 / Discover</div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-3" data-testid="discover-title">
          Source new prospects<span className="text-[#CCFF00]">.</span>
        </h1>
        <p className="text-[#8a8a8a] mt-4 max-w-2xl">
          Describe the angle you want to explore. The model uses your profile + these keywords to surface plausible
          companies and decision-makers worth approaching.
        </p>
      </header>

      <section className="px-12 py-10 border-b border-[#222] grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <Field label="Focus / keywords" hint="e.g. luxury retail flagship, generative typography, immersive museum">
            <input
              data-testid="discover-keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Brand activation · projection mapping · launch event"
              className="w-full px-3 py-3 text-sm bg-transparent"
            />
          </Field>

          <Field label="Industries (comma-separated, optional)">
            <input
              data-testid="discover-industries"
              value={industries}
              onChange={(e) => setIndustries(e.target.value)}
              placeholder="Fashion, Museums, Music tech"
              className="w-full px-3 py-3 text-sm"
            />
          </Field>

          <Field label="Location preferences (in order — pref 1 gets strongest weight)">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                data-testid="discover-loc-1"
                value={loc1}
                onChange={(e) => setLoc1(e.target.value)}
                placeholder="Preference 1 · e.g. New York"
                className="px-3 py-3 text-sm"
              />
              <input
                data-testid="discover-loc-2"
                value={loc2}
                onChange={(e) => setLoc2(e.target.value)}
                placeholder="Preference 2 · e.g. London"
                className="px-3 py-3 text-sm"
              />
              <input
                data-testid="discover-loc-3"
                value={loc3}
                onChange={(e) => setLoc3(e.target.value)}
                placeholder="Preference 3 · e.g. Tokyo"
                className="px-3 py-3 text-sm"
              />
            </div>
          </Field>

          <Field label="How many prospects?">
            <input
              data-testid="discover-count"
              type="number"
              min={1}
              max={12}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="w-32 px-3 py-3 text-sm"
            />
          </Field>

          <button
            onClick={onRun}
            disabled={loading}
            data-testid="discover-run-btn"
            className="btn-accent px-8 py-4 inline-flex items-center gap-3 text-[12px] uppercase tracking-widest disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" strokeWidth={1.5} />
            ) : (
              <Sparkles size={16} strokeWidth={1.5} />
            )}
            {loading ? "Sourcing…" : "Run discovery"}
          </button>
        </div>

        <aside className="lg:col-span-4 border border-[#222] p-6 self-start">
          <div className="uppercase-mono text-[#8a8a8a] mb-3">How it works</div>
          <ol className="space-y-3 text-sm text-[#ededed]">
            <li>
              <span className="text-[#CCFF00]">01 ·</span> Claude crafts targeted web-search queries from your brief.
            </li>
            <li>
              <span className="text-[#CCFF00]">02 ·</span> Tavily searches the live web — press, launches, hires, festival rosters.
            </li>
            <li>
              <span className="text-[#CCFF00]">03 ·</span> Claude extracts real people/companies from the snippets — no inventions, every lead cites source URLs.
            </li>
          </ol>
          <div className="uppercase-mono text-[#555] mt-6">source · Tavily + Claude</div>
        </aside>
      </section>

      <section className="px-12 py-10">
        <div className="uppercase-mono text-[#8a8a8a] mb-6">This batch · {results.length}</div>
        {results.length === 0 && !loading && (
          <div className="border border-dashed border-[#222] p-12 text-center" data-testid="discover-empty">
            <div className="font-serif text-3xl text-[#8a8a8a]">No batch yet.</div>
            <div className="uppercase-mono text-[#555] mt-2">Run discovery to populate</div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
          {results.map((l) => (
            <button
              key={l.id}
              data-testid={`discover-result-${l.id}`}
              onClick={() => navigate(`/app/leads/${l.id}`)}
              className="text-left bg-[#0A0A0A] hover:bg-[#121212] p-6 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-serif text-2xl leading-tight">{l.name}</div>
                <div className="font-mono text-[#CCFF00] text-sm">{l.fit_score}</div>
              </div>
              <div className="uppercase-mono text-[#8a8a8a] mt-2">
                {l.role || "—"} · {l.company}
              </div>
              <div className="flex items-center gap-2 mt-3">
                {l.source === "tavily" && (
                  <span className="text-[10px] uppercase tracking-widest bg-[#CCFF00] text-black px-2 py-0.5">
                    ✓ Tavily · {l.source_urls?.length || 0} sources
                  </span>
                )}
                {l.source === "ai" && (
                  <span className="text-[10px] uppercase tracking-widest border border-[#FFB800] text-[#FFB800] px-2 py-0.5">
                    AI · unverified
                  </span>
                )}
              </div>
              <div className="text-[12px] text-[#ededed] mt-4 leading-relaxed line-clamp-3">{l.fit_reason}</div>
              {l.signals?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {l.signals.slice(0, 3).map((s, i) => (
                    <span key={`${l.id}-signal-${i}-${s}`} className="text-[10px] uppercase tracking-widest border border-[#222] px-2 py-1 text-[#8a8a8a]">
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="uppercase-mono text-[#8a8a8a] mb-2">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-[#555] mt-1 font-mono">{hint}</div>}
    </label>
  );
}
